/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { useDispatch, useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import {
  ASSIGN_TO_LOCATION,
  assignedUsersDefaultSearchParams,
  DELETE,
  DIALOUGE_CANCEL,
  DISASSOCIATE,
  DISASSOCIATE_TITLE,
  DISASSOCIATED_LOCATION,
  EMPTY_LOCATIONS_DISPLAY_MESSAGE,
  ROLE_LOCATION_ASSIGNMENT_INFO,
  ROWS_PER_PAGE_LABEL,
  SNACKBAR_ALERT_DEFAULT_PROPS
} from './constants';
import { useStyles } from './styles';
import { Button, Grid, IconButton } from '@mui/material';
import { LuiActionsMenu } from '@jda/lui-common-component-library-mui5';
import { Trash } from '@jda/lui-common-icon-library-mui5';
import { MRT_Row, MRT_RowData } from 'material-react-table';
import Loader from '../../shared/Loader';
import EmptyComponent from '../../shared/EmptyComponent/EmptyComponent';
import { AlertDialog } from '../../shared/Dialog/AlertDialog';
import SnackbarAlert from '../../shared/SnackbarAlert';
import { ErrorMessage } from '../../shared/ErrorMessage/ErrorMessage';
import { IRoleSelected } from '../../models/Roles/RolesContracts';
import { fetchLocationsToRole } from '../../store/role/LocationToRole/locations';
import { IAssignedUsersDefaultParamsSearch } from '../../models/AssignedUsers/AssignedUsers';
import { PAGE_NUMBER, PAGE_SIZE } from '../../container/Users/constants';
import {
  selectLocations,
  selectLocationsError,
  selectLocationsLoading,
  selectLocationsPaginationDetails
} from '../../store/role/LocationToRole/locationsToRoleSelector';
import ManageLocationsDialog from '../../shared/ManageLocationsDialog/ManageLocationsDialog';
import { associateDisassociateLocationToRole } from '../../services/roles';
import CustomTablePagination from '../../shared/CustomTablePagination';
import { setSnackBarMessage } from '../../store/snackBar/snackBarActionCreator';
import { selectResourcePermissions } from '../../store/auth/authSelector';
import { checkResourcePermission } from '../../shared/CheckPermissions/CheckPermission';
import { actions as resourceActions, resources } from '../../shared/CheckPermissions/constants';
export const assignedLocationsDefaultSearchParams: IAssignedUsersDefaultParamsSearch = {
  'page-number': PAGE_NUMBER,
  'page-size': PAGE_SIZE
};

const Location = ({ selectedItem }: IRoleSelected) => {
  const locations = useSelector(selectLocations);
  const loading = useSelector(selectLocationsLoading);
  const error = useSelector(selectLocationsError);
  const paginationDetails = useSelector(selectLocationsPaginationDetails);
  const resourcePermissions = useSelector(selectResourcePermissions);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [rowData, setRowData] = useState<any>();
  const [openModal, setOpenCloseModal] = useState(false);
  const [showDialogLoader, setShowDialogLoader] = useState(false);
  const dispatch = useDispatch();
  const { classes } = useStyles();

  const getAllLocations = useCallback(() => {
    fetchLocationsToRole(dispatch, selectedItem.id, assignedLocationsDefaultSearchParams);
  }, [dispatch, selectedItem.id]);

  useEffect(() => {
    getAllLocations();
  }, [getAllLocations, selectedItem.id]);

  const editRolePolicy =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.EDIT, resourcePermissions.resources[resources.ROLES]);
  const assignLocationPolicy =
    resourcePermissions &&
    resourcePermissions.resources &&
    checkResourcePermission(resourceActions.ASSIGN, resourcePermissions.resources[resources.LOCATIONS]);

  const isLocationManagementAllowed = Boolean(editRolePolicy && assignLocationPolicy);

  const columns = [
    {
      header: 'Sub-Realm',
      id: 'name',
      accessorFn: (rowData: MRT_RowData) => {
        return rowData.name || '';
      },
      muiTableHeadCellProps: {
        sx: {
          width: `${90 * 8}%`
        }
      }
    }
  ];

  const actions = isLocationManagementAllowed
    ? (row: MRT_Row<MRT_RowData>) => (
      <LuiActionsMenu
        items={[
          {
            render: (
              <IconButton
                aria-label={DELETE}
                onClick={() => {
                  deleteLocationOnClickDialog(row.original);
                }}
                size="small"
              >
                <Trash />
              </IconButton>
            ),
            alwaysVisible: true
          }
        ]}
        maxItemsToDisplay={1}
      />
    )
    : undefined;

  const deleteLocationOnClickDialog = (rowData: any) => {
    setIsDeleteDialogOpen(true);
    setRowData(rowData);
  };

  const onChangePageOptions = (itemsPerPage: number, page: number) => {
    const search = { ...assignedUsersDefaultSearchParams, 'page-number': page, 'page-size': itemsPerPage };
    fetchLocationsToRole(dispatch, selectedItem.id, search);
  };

  const handleSaveLocations = async (toAdd: any, toRemove: any, selectedItem: any) => {
    await associateDisassociateLocationToRole(
      selectedItem,
      [
        toAdd.length > 0 ? { op: 'add', value: toAdd.map((location: any) => location.name) } : [],
        toRemove.length > 0 ? { op: 'remove', value: toRemove.map((location: any) => location.name) } : []
      ].flat()
    );
    await fetchLocationsToRole(dispatch, selectedItem, {
      ...assignedLocationsDefaultSearchParams,
      'page-number': paginationDetails.pageNumber,
      'page-size': paginationDetails.pageSize
    });
  };

  const handleDeleteLocation = async () => {
    setShowDialogLoader(true);
    try {
      await associateDisassociateLocationToRole(selectedItem.id, [{ op: 'remove', value: [rowData.name] }]);
      setIsDeleteDialogOpen(false);
      setShowDialogLoader(false);
      await fetchLocationsToRole(dispatch, selectedItem.id, {
        ...assignedLocationsDefaultSearchParams,
        'page-number': paginationDetails.pageNumber,
        'page-size': paginationDetails.pageSize
      });
    } catch (err: any) {
      setShowDialogLoader(false);
      dispatch(
        setSnackBarMessage({
          ...SNACKBAR_ALERT_DEFAULT_PROPS,
          message: err.message,
          severity: 'error'
        })
      );
    }
  };

  if (loading) {
    return <Loader showLoader={loading} isCenter />;
  }

  return (
    <Grid container>
      {!loading && (
        <Grid container>
          <Grid id="policy-card-component-container" item xs={12} sm={12}>
            {locations.length > 0 && (
              <Grid container className={classes.controlPanel}>
                {isLocationManagementAllowed && (
                  <Grid item xs={12} className={classes.actionBlock}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="primary"
                      className={classes.margin}
                      onClick={() => setOpenCloseModal(!openModal)}
                      data-testid="assign-to-locations-button"
                    >
                      {ASSIGN_TO_LOCATION}
                    </Button>
                  </Grid>
                )}
              </Grid>
            )}
            <Grid container className={classes.card}>
              <Grid item xs={12} sm={12}>
                {locations.length > 0 ? (
                  <CustomTablePagination
                    columns={[...columns]}
                    data={locations}
                    data-testid="locations-to-role-table"
                    rowsPerPageOptions={[20, 50, 100]}
                    totalCount={paginationDetails.totalCount}
                    labelRowsPerPage={ROWS_PER_PAGE_LABEL}
                    onChangePageOptions={onChangePageOptions}
                    currentPageNumber={paginationDetails.pageNumber}
                    currentItemsPerPage={paginationDetails.pageSize}
                    customRowActions={actions}
                  />
                ) : (
                  <EmptyComponent
                    primaryButtonText={ASSIGN_TO_LOCATION}
                    text={EMPTY_LOCATIONS_DISPLAY_MESSAGE}
                    title={ROLE_LOCATION_ASSIGNMENT_INFO}
                    disableCreateAction={!isLocationManagementAllowed}
                    handleOnCreateAction={() => {
                      setOpenCloseModal(!openModal);
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {isDeleteDialogOpen && (
        <AlertDialog
          dialogTitle={DISASSOCIATE_TITLE}
          dialogContentText={DISASSOCIATED_LOCATION}
          recordName={rowData.name || ''}
          primaryButtonText={DISASSOCIATE}
          secondButtonText={DIALOUGE_CANCEL}
          handleOnCancel={() => setIsDeleteDialogOpen(false)}
          handleOnOk={() => handleDeleteLocation()}
          showDialogLoader={showDialogLoader}
          hideCanNotBeUndoneMessage={true}
        />
      )}
      {openModal && (
        <ManageLocationsDialog
          maxToAdd={50}
          maxToRemove={50}
          onSave={async (toAdd, toRemove) => handleSaveLocations(toAdd, toRemove, selectedItem.id)}
          onClose={() => {
            setOpenCloseModal(!openModal);
          }}
        />
      )}

      <SnackbarAlert />
      {error && <ErrorMessage {...error} />}
    </Grid>
  );
};

export default Location;
